import React, { useState, useEffect } from 'react';
import PageSkeleton from '../common/Skeleton';
import Pagination from '../common/Pagination';
import { Trash2, Clock, Shield, Megaphone, UserX, UserCheck } from 'lucide-react';
import axios from 'axios';
import { IconButton, DataTable, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell, EmptyState, StatusBadge, FormField, Input, Select } from '../common/ui';
import { formatDateTime } from '../../utils/dateUtils';
import { getUserPreference, setUserPreference } from '../../hooks/useUserPreferences';
import { useAuth } from '../../contexts/AuthContext';

// Users without the field (older API responses) count as active
const isUserActive = (user) => user.is_active !== false;

const Users = () => {
  const { user: currentUser } = useAuth();
  const isCurrentUser = (user) => !!currentUser && String(user.id) === String(currentUser.id);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(() => parseInt(getUserPreference('users', 'itemsPerPage', '50')));
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    time_tracking_enabled: false,
    time_master: false,
    repair_reports_enabled: false,
    is_active: true
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async (page = 1, limit = itemsPerPage) => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', limit.toString());
      
      const response = await axios.get(`/api/users?${params.toString()}`);
      
      // Handle paginated response
      const usersData = response.data.users || response.data;
      
      if (Array.isArray(usersData)) {
        setUsers(usersData);
        
        // Handle pagination metadata
        if (response.data.pagination) {
          setTotalPages(response.data.pagination.totalPages || 1);
          setTotalItems(response.data.pagination.totalItems || usersData.length);
          setCurrentPage(response.data.pagination.currentPage || page);
        } else {
          // Fallback for backends that don't return pagination metadata
          setTotalPages(usersData.length === limit ? page + 1 : page);
          setTotalItems(usersData.length);
          setCurrentPage(page);
        }
      } else {
        setUsers(usersData);
        // For non-paginated response, set defaults
        setTotalPages(1);
        setTotalItems(usersData.length || 0);
        setCurrentPage(1);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to fetch users');
      setUsers([]);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'user',
      time_tracking_enabled: false,
      time_master: false,
      repair_reports_enabled: false,
      is_active: true
    });
    setEditingUser(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        // For editing, only send password if it's been changed
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }
        await axios.put(`/api/users/${editingUser.id}`, updateData);
        setSuccess('User updated successfully');
      } else {
        await axios.post('/api/users', formData);
        setSuccess('User created successfully');
      }
      
      setShowModal(false);
      resetForm();
      fetchUsers();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving user:', error);
      const errorMessage = error.response?.data?.error || 'Failed to save user';
      setError(errorMessage);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      time_tracking_enabled: user.time_tracking_enabled || false,
      time_master: user.time_master || false,
      repair_reports_enabled: user.repair_reports_enabled || false,
      is_active: isUserActive(user)
    });
    setShowModal(true);
  };

  const handleToggleActive = async (user) => {
    const activate = !isUserActive(user);
    if (!activate && !window.confirm(
      `Deactivate ${user.name}?\n\n` +
      'They are logged out right away and cannot log in again. ' +
      'Their time entries, devices and repairs are kept.'
    )) {
      return;
    }

    try {
      await axios.put(`/api/users/${user.id}`, { is_active: activate });
      setSuccess(activate ? 'User activated' : 'User deactivated');
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error changing user status:', error);
      setError(error.response?.data?.error || 'Failed to change user status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }
    
    try {
      await axios.delete(`/api/users/${id}`);
      setSuccess('User deleted successfully');
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting user:', error);
      setError(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      fetchUsers(newPage);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setUserPreference('users', 'itemsPerPage', newItemsPerPage);
    setCurrentPage(1);
    fetchUsers(1, newItemsPerPage);
  };


  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Users</h1>
        <PageSkeleton rows={3} rowHeight="h-16" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Users</h1>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-naviter-blue hover:bg-naviter-blue-dark text-white px-4 py-2 rounded-lg flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          New User
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <p className="text-green-800">{success}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Users Table */}
      <DataTable
        emptyState={users.length === 0 ? (
          <EmptyState message="No users found. Create your first user!" />
        ) : null}
      >
        <TableHeader>
          <tr>
            <TableHeaderCell>Name</TableHeaderCell>
            <TableHeaderCell>Email</TableHeaderCell>
            <TableHeaderCell>Role</TableHeaderCell>
            <TableHeaderCell>Settings</TableHeaderCell>
            <TableHeaderCell>Created</TableHeaderCell>
            <TableHeaderCell>Actions</TableHeaderCell>
          </tr>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id} className={isUserActive(user) ? '' : 'opacity-60'}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">{user.name}</span>
                  {!isUserActive(user) && (
                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Not active</span>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="text-sm text-gray-900">
                  {user.email}
                </div>
              </TableCell>
              <TableCell>
                <StatusBadge status={user.role} variant="role" />
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5">
                  {user.time_tracking_enabled && (
                    <Clock size={16} className="text-naviter-blue" title="Time tracking enabled" />
                  )}
                  {user.time_master && (
                    <Shield size={16} className="text-amber-500" title="Time master" />
                  )}
                  {user.repair_reports_enabled && (
                    <Megaphone size={16} className="text-green-600" title="Receives repair reports" />
                  )}
                </div>
              </TableCell>
              <TableCell className="text-gray-500">
                {formatDateTime(user.created_at)}
              </TableCell>
              <TableCell className="text-sm font-medium">
                <button
                  onClick={() => handleEdit(user)}
                  className="text-blue-600 hover:text-blue-900 mr-3"
                >
                  Edit
                </button>
                {!isCurrentUser(user) && (
                  <IconButton
                    icon={isUserActive(user) ? UserX : UserCheck}
                    onClick={() => handleToggleActive(user)}
                    title={isUserActive(user) ? 'Deactivate user' : 'Activate user'}
                  />
                )}
                {user.role !== 'admin' && (
                  <IconButton
                    variant="delete"
                    icon={Trash2}
                    onClick={() => handleDelete(user.id)}
                    title="Delete user"
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </DataTable>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 lg:w-1/3 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingUser ? 'Edit User' : 'New User'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <FormField label="Name" required>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter full name"
                  />
                </FormField>

                <FormField label="Email" required>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter email"
                  />
                </FormField>

                <FormField 
                  label={`Password ${editingUser ? '(leave blank to keep current)' : ''}`}
                  required={!editingUser}
                >
                  <Input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required={!editingUser}
                    placeholder={editingUser ? 'Enter new password or leave blank' : 'Enter password'}
                  />
                </FormField>

                <FormField label="Role">
                  <Select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    options={[
                      { value: 'user', label: 'User' },
                      { value: 'technician', label: 'Technician' },
                      { value: 'accounting', label: 'Accounting' },
                      { value: 'admin', label: 'Admin' }
                    ]}
                  />
                </FormField>

                {editingUser && (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_active"
                      name="is_active"
                      checked={formData.is_active}
                      disabled={isCurrentUser(editingUser)}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="h-4 w-4 text-naviter-blue border-gray-300 rounded focus:ring-naviter-blue disabled:opacity-50"
                    />
                    <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                      Active
                    </label>
                    <span className="text-xs text-gray-400">
                      {isCurrentUser(editingUser)
                        ? '(you cannot deactivate your own account)'
                        : '(inactive users cannot log in; their data is kept)'}
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="time_tracking_enabled"
                    name="time_tracking_enabled"
                    checked={formData.time_tracking_enabled}
                    onChange={(e) => setFormData(prev => ({ ...prev, time_tracking_enabled: e.target.checked }))}
                    className="h-4 w-4 text-naviter-blue border-gray-300 rounded focus:ring-naviter-blue"
                  />
                  <label htmlFor="time_tracking_enabled" className="text-sm font-medium text-gray-700">
                    Enable Time Tracking
                  </label>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="time_master"
                    name="time_master"
                    checked={formData.time_master}
                    onChange={(e) => setFormData(prev => ({ ...prev, time_master: e.target.checked }))}
                    className="h-4 w-4 text-amber-500 border-gray-300 rounded focus:ring-amber-500"
                  />
                  <label htmlFor="time_master" className="text-sm font-medium text-gray-700">
                    Time Master
                  </label>
                  <span className="text-xs text-gray-400">(can view &amp; edit all entries)</span>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="repair_reports_enabled"
                    name="repair_reports_enabled"
                    checked={formData.repair_reports_enabled}
                    onChange={(e) => setFormData(prev => ({ ...prev, repair_reports_enabled: e.target.checked }))}
                    className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-600"
                  />
                  <label htmlFor="repair_reports_enabled" className="text-sm font-medium text-gray-700">
                    Receive Repair Reports
                  </label>
                  <span className="text-xs text-gray-400">(gets "Report to the team" emails)</span>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-naviter-blue text-white rounded-md hover:bg-naviter-blue-dark"
                  >
                    {editingUser ? 'Update User' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
